import { type FC, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import useMeasure from 'react-use-measure';
import InfoIcon from '@assets/img/icons/info-2.svg?react';
import StarIcon from '@assets/img/icons/star.svg?react';
import {
  GoodsItem,
  LockGoodsItem,
} from '@components/Goods/GoodsItem/GoodsItem';
import { WorkshopContext } from '@pages/Workshop/context';
import { selectContractItemList } from '@redux/selectors/contractSelectors';
import { selectGoodsListByWorkshop } from '@redux/selectors/workshopSelectors';
import { useAppSelector } from '@redux/store';
import cn from 'classnames';
import { motion } from 'framer-motion';
import { type Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { GoodsDetail } from '@/components/Goods/GoodsDetail/GoodsDetail';
import { StorageIndicator } from '@/components/ui/StorageIndicator/StorageIndicator';

import { GoodsDetailContext } from '../context';

interface GoodsSwiperProps {
  workshopId: string;
}

const GoodsSwiper: FC<GoodsSwiperProps> = ({ workshopId }) => {
  const { activeGoodsName, activeGoods, setActiveGoodsName } =
    useContext(WorkshopContext);
  const contractItems = useAppSelector(selectContractItemList);
  const { id } = useParams();
  const [detailWrapperRef, detailWrapperMeasure] = useMeasure();
  const [firstSwiper, setFirstSwiper] = useState<SwiperInstance>();
  const goodsData = useAppSelector((state) =>
    selectGoodsListByWorkshop(state, id),
  );
  const { setIsOpen, animateTitle } = useContext(GoodsDetailContext);
  const [detailIsOpen, setDetailIsOpen] = useState(false);
  const initialIndex = useMemo(() => {
    const index = goodsData?.list.findIndex((goods) =>
      goods?.name ? goods?.name === activeGoodsName : false,
    );

    return index === undefined || index < 0 ? undefined : index;
  }, [goodsData?.list, activeGoodsName]);

  const onSlideChange = (swiper: SwiperInstance): void => {
    const activeIndex = swiper.activeIndex;
    const activeGoodsElem = goodsData?.list
      ? goodsData.list[activeIndex]
      : undefined;

    if (activeGoodsElem) {
      setActiveGoodsName(activeGoodsElem?.name);
    }

    const lastAvailableIndex = (goodsData?.availableQuantity || 0) - 1;

    if (activeIndex > lastAvailableIndex) {
      swiper.slideTo(lastAvailableIndex);
    }
  };

  return (
    activeGoodsName &&
    initialIndex !== undefined && (
      <Swiper
        onSwiper={setFirstSwiper}
        className={cn('!overflow-visible')}
        centeredSlides={true}
        slidesPerView="auto"
        onSlideChange={onSlideChange}
        loop={false}
        draggable={false}
        initialSlide={initialIndex}
        style={{
          height: '9rem',
        }}
      >
        {activeGoods && (
          <motion.div
            onClick={() => {
              const middle =
                detailWrapperMeasure.height / 2 + detailWrapperMeasure.y;
              animateTitle(middle);
              setIsOpen(true);
            }}
            className={'absolute top-0 left-1/2 -translate-x-1/2 z-10'}
          >
            <GoodsDetail
              slideList={goodsData?.list.filter((data) => !!data)}
              ref={detailWrapperRef}
              detailIsOpen={detailIsOpen}
              setDetailIsOpen={setDetailIsOpen}
              name={activeGoods.name}
              workshopId={workshopId}
              costPerUnit={activeGoods?.costPerUnit}
              productionRate={activeGoods?.productionRateOriginal}
              firstSwiper={firstSwiper}
              initialSlide={initialIndex}
            />
          </motion.div>
        )}

        {goodsData?.list?.map((goods, index) => {
          const lvl = index * 5;

          return (
            <>
              <SwiperSlide
                className={cn(
                  'flex items-center justify-center',
                  !goods && 'swiper-no-swiping',
                  firstSwiper?.activeIndex === index && 'z-[9]',
                )}
                style={{
                  height: '100%',
                  width: '9rem',
                }}
                key={index}
              >
                {({ isActive }) => (
                  <div
                    className="relative"
                    onClick={() => {
                      if (goods && !isActive) {
                        firstSwiper?.slideTo(index);
                      }
                    }}
                  >
                    {goods ? (
                      <GoodsItem
                        radius="large"
                        variant="production"
                        size={{
                          width: isActive ? '9rem' : '4rem',
                          height: isActive ? '9rem' : '4rem',
                        }}
                        kind={goods.name}
                      >
                        {contractItems?.includes(goods.name) && (
                          <StarIcon
                            className={cn(
                              'absolute top-2 left-2 shrink-0 text-gold',
                              isActive ? 'w-4 h-4' : 'w-2 h-2',
                            )}
                          />
                        )}
                        {isActive && (
                          <>
                            <InfoIcon className="absolute text-white/20 top-2 right-2" />
                            <StorageIndicator
                              className="absolute left-1/2 -translate-x-1/2 bottom-2"
                              resource={goods.name}
                            />
                          </>
                        )}
                      </GoodsItem>
                    ) : (
                      <LockGoodsItem
                        classNames={{
                          icon: '!w-4 !h-4',
                          wrapper: '!rounded-2xl',
                        }}
                        lvl={lvl}
                        size={{
                          width: '4rem',
                          height: '4rem',
                        }}
                      />
                    )}
                  </div>
                )}
              </SwiperSlide>
            </>
          );
        })}
      </Swiper>
    )
  );
};

export { GoodsSwiper };
